import { createServerFn } from "@tanstack/react-start";
import { setResponseHeaders } from "@tanstack/react-start/server";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type EkonomimLatest = {
  title: string;
  url: string;
  date: string;
  excerpt: string;
} | null;

const AUTHOR_URL =
  "https://www.ekonomigazetesi.com/yazar/arda-oztaskin-34322";

function stripTags(s: string): string {
  return s
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&#8217;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&#8220;/g, "“")
    .replace(/&#8221;/g, "”")
    .replace(/&hellip;/g, "…")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

async function fetchExcerpt(url: string): Promise<string> {
  try {
    const res = await fetch(url, {
      headers: { "User-Agent": "Mozilla/5.0 (compatible; akillisurdurulebilirlik/1.0)" },
    });
    if (!res.ok) return "";
    const html = await res.text();

    const ld = html.match(
      /<script[^>]+type="application\/ld\+json"[^>]*>([\s\S]*?)<\/script>/g,
    );
    if (ld) {
      for (const block of ld) {
        const inner = block.replace(/^<script[^>]*>/, "").replace(/<\/script>$/, "");
        try {
          const parsed = JSON.parse(inner) as { articleBody?: string };
          if (typeof parsed.articleBody === "string" && parsed.articleBody.length > 40) {
            const paragraphs = parsed.articleBody.match(/<p[^>]*>([\s\S]*?)<\/p>/g) ?? [];
            for (const p of paragraphs) {
              const txt = stripTags(p);
              if (txt.length > 40) {
                return txt.slice(0, 260) + (txt.length > 260 ? "…" : "");
              }
            }
            const flat = stripTags(parsed.articleBody);
            if (flat.length > 40) return flat.slice(0, 260) + "…";
          }
        } catch {
          // ignore non-article JSON-LD blocks
        }
      }
    }

    const entry = html.match(
      /<div[^>]*class="[^"]*entry-content[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<(?:footer|div class="entry-footer|aside)/,
    );
    const body = entry ? entry[1] : html;
    const paragraphs = body.match(/<p[^>]*>([\s\S]*?)<\/p>/g) ?? [];
    for (const p of paragraphs) {
      const txt = stripTags(p);
      if (txt.length < 60) continue;
      if (/»/.test(txt)) continue;
      if (/^(paylaş|share|yorum)/i.test(txt)) continue;
      return txt.slice(0, 260) + (txt.length > 260 ? "…" : "");
    }
    return "";
  } catch {
    return "";
  }
}

async function googleTrToEn(text: string): Promise<string> {
  if (!text.trim()) return text;
  const googleTranslate = async () => {
    const qs = new URLSearchParams({ client: "gtx", sl: "tr", tl: "en", dt: "t", q: text });
    const res = await fetch(`https://translate.googleapis.com/translate_a/single?${qs.toString()}`, {
      headers: { "User-Agent": "Mozilla/5.0" },
    });
    if (!res.ok) throw new Error(`Google translate failed [${res.status}]`);
    const json = (await res.json()) as Array<Array<Array<string>>>;
    return (json[0] ?? []).map((p) => p[0]).join("").trim() || text;
  };

  try {
    return await googleTranslate();
  } catch {
    // fall through
  }

  try {
    const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
    if (!LOVABLE_API_KEY) return text;
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${LOVABLE_API_KEY}`,
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash-lite",
        messages: [
          {
            role: "system",
            content:
              "Translate the user's Turkish text into natural, fluent English. Keep proper nouns. Return ONLY the translated text.",
          },
          { role: "user", content: text },
        ],
      }),
    });
    if (!res.ok) return text;
    const json = (await res.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const out = json.choices?.[0]?.message?.content?.trim();
    return out && out.length > 0 && out !== text ? out : text;
  } catch {
    return text;
  }
}

/**
 * Scrape the Ekonomim author page for the latest column (TR) and produce
 * an English translation. Used by the scheduled refresh route.
 */
export async function scrapeLatestEkonomimColumn(): Promise<{
  title_tr: string;
  title_en: string;
  excerpt_tr: string;
  excerpt_en: string;
  url: string;
  published_at: string;
} | null> {
  try {
    const res = await fetch(AUTHOR_URL, {
      headers: { "User-Agent": "Mozilla/5.0 (compatible; akillisurdurulebilirlik/1.0)" },
    });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(
      /<h3 class="entry-title">\s*<a class="p-url" href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/,
    );
    if (!m) return null;
    const url = m[1];
    const title_tr = stripTags(m[2]);
    const after = html.slice(html.indexOf(m[0]));
    const dm = after.match(/<time class="date published"[^>]*>([^<]+)<\/time>/);
    const published_at = dm ? dm[1].trim() : "";
    const excerpt_tr = await fetchExcerpt(url);
    const [title_en, excerpt_en] = await Promise.all([
      googleTrToEn(title_tr),
      excerpt_tr ? googleTrToEn(excerpt_tr) : Promise.resolve(""),
    ]);
    return { title_tr, title_en, excerpt_tr, excerpt_en, url, published_at };
  } catch {
    return null;
  }
}

function publicSupabase() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const getLatestEkonomimColumn = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ lang: z.enum(["tr", "en"]).default("tr"), v: z.number().optional() }).parse(d))
  .handler(async ({ data }): Promise<EkonomimLatest> => {
    setResponseHeaders(
      new Headers({
        "Cache-Control": "public, max-age=300, stale-while-revalidate=86400",
      }),
    );
    // Prefer the DB cache (refreshed weekly by pg_cron on Wednesday 08:00 Istanbul).
    try {
      const sb = publicSupabase();
      const { data: row } = await sb
        .from("ekonomim_weekly_cache")
        .select("title_tr, title_en, excerpt_tr, excerpt_en, url, published_at")
        .eq("id", 1)
        .maybeSingle();
      if (row && row.url) {
        return {
          title: (data.lang === "en" ? row.title_en : row.title_tr) || row.title_tr,
          excerpt: (data.lang === "en" ? row.excerpt_en : row.excerpt_tr) || row.excerpt_tr,
          date: row.published_at,
          url: row.url,
        };
      }
    } catch {
      // fall through to live scrape
    }
    // Fallback: live scrape (first load before cron has ever run).
    const scraped = await scrapeLatestEkonomimColumn();
    if (!scraped) return null;
    return {
      title: data.lang === "en" ? scraped.title_en : scraped.title_tr,
      excerpt: data.lang === "en" ? scraped.excerpt_en : scraped.excerpt_tr,
      date: scraped.published_at,
      url: scraped.url,
    };
  });
